import { useEffect, useMemo, useState } from 'react'
import type { WizardStepProps } from '../../../../types/builder.types'
import { executeComponentQuery } from '../../../../utils/query-builder'
import {
  getCustomQueryConfigSignature,
  normalizeQueryResultData,
} from '../../../../utils/custom-query-builder-fields'
import { KPICard } from '../../../visualizations/KPICard'
import { TimeSeriesChart } from '../../../visualizations/TimeSeriesChart'
import { BarChartComponent } from '../../../visualizations/BarChartComponent'
import { DataTable } from '../../../visualizations/DataTable'
// Fase 1: Componentes críticos
import { MapComponent } from '../../../visualizations/MapComponent'
import { PieChartComponent } from '../../../visualizations/PieChartComponent'
import { GaugeComponent } from '../../../visualizations/GaugeComponent'
// Fase 2: Componentes muy útiles
import { AreaChartComponent } from '../../../visualizations/AreaChartComponent'
import { StackedBarComponent } from '../../../visualizations/StackedBarComponent'
import { ScatterPlotComponent } from '../../../visualizations/ScatterPlotComponent'
// Fase 3: Nice to have
import { HeatmapComponent } from '../../../visualizations/HeatmapComponent'
import { TreemapComponent } from '../../../visualizations/TreemapComponent'
import { RadarChartComponent } from '../../../visualizations/RadarChartComponent'
import { RadialBarComponent } from '../../../visualizations/RadialBarComponent'

export function PreviewStep({ config, onNext, onPrev }: WizardStepProps) {
  const [previewData, setPreviewData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  

  const previewFetchKey = useMemo(() => {
    return JSON.stringify({
      type: config.componentType,
      dataSource: config.dataSource,
      cqSig: getCustomQueryConfigSignature(config.customQuery),
      sql: config.customQuery?.sql ?? '',
      stackedBar: config.stackedBarConfig,
      chart: config.chartConfig,
      pie: config.pieConfig,
      area: config.areaChartConfig,
    })
  }, [config])

  useEffect(() => {
    generatePreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clave estable evita refetch por referencia nueva de `config`
  }, [previewFetchKey])

  const generatePreview = async () => {
    setLoading(true)
    setError(null)

    try {

      const raw = await executeComponentQuery(config)
      setPreviewData(
        normalizeQueryResultData(raw, config.componentType || 'table')
      )
    } catch (err) {
      console.error('Error generando preview:', err)
      const msg =
        err instanceof Error && err.message
          ? `Error al cargar la vista previa: ${err.message}`
          : 'Error al cargar los datos de vista previa'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const renderPreview = () => {
    if (loading) return <div className="animate-pulse bg-gray-200 h-48 rounded"></div>
    if (error) return <div className="text-red-600 p-4 border border-red-200 rounded">{error}</div>

    switch (config.componentType) {
      case 'kpi':
        return (
          <KPICard
            title={config.metadata?.title || 'Sin título'}
            value={previewData || 0}
            unit={config.styling?.unit}
            icon={config.styling?.icon}
            color={config.styling?.color}
            loading={loading}
          />
        )

      case 'line-chart':
        return (
          <TimeSeriesChart
            data={previewData || []}
            dataKey={config.chartConfig?.yAxisKey || ''}
            xAxisKey={config.chartConfig?.xAxisKey}
            title={config.metadata?.title}
            color={config.chartConfig?.chartColor}
            unit={config.styling?.unit}
            loading={loading}
          />
        )

      case 'bar-chart': {
        const barAliases = config.customQuery?.config?.fieldAliases as
          | Record<string, string>
          | undefined
        const resolveBarKey = (k: string) => (barAliases && barAliases[k]) || k
        return (
          <BarChartComponent
            data={previewData || []}
            xKey={resolveBarKey(config.chartConfig?.xAxisKey || '')}
            yKey={resolveBarKey(config.chartConfig?.yAxisKey || '')}
            title={config.metadata?.title}
            color={config.chartConfig?.chartColor}
            horizontal={config.chartConfig?.horizontal}
            loading={loading}
          />
        )
      }

      case 'table':
        // Usar col.key tal cual para que coincida con las claves de los datos (p. ej. municipios.nombre)
        const tableColumns: any = (config.tableConfig?.columns || []).map((col: any) => {
          const lab = col.label ?? col.key ?? ''
          const labelStr = typeof lab === 'string' ? lab : String(lab)
          return {
          key: col.key,
          label: labelStr.includes('.')
            ? labelStr.split('.').pop()!.replace(/_/g, ' ')
            : labelStr.replace(/_/g, ' '),
          sortable: col.sortable,
          format: col.format,
        }
        });
        return (
          <DataTable
            data={previewData || []}
            columns={tableColumns}
            title={config.metadata?.title}
            pageSize={config.tableConfig?.pageSize}
            loading={loading}
          />
        )

      // =====================================================
      // FASE 1: COMPONENTES CRÍTICOS
      // =====================================================

      case 'map':
        return (
          <MapComponent
            title={config.metadata?.title || 'Sin título'}
            geojsonData={previewData && previewData.length > 0 ? previewData[0] : null}
            mapType={config.mapConfig?.mapType}
            centerLat={config.mapConfig?.centerLat}
            centerLon={config.mapConfig?.centerLon} 
            zoom={config.mapConfig?.zoom}
          />
        )

      case 'pie-chart':
        return (
          <PieChartComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            dataKey={config.pieConfig?.dataKey || 'value'}
            nameKey={config.pieConfig?.nameKey || 'name'}
            showPercentage={config.pieConfig?.showPercentage}
            innerRadius={config.pieConfig?.innerRadius}
            colors={config.pieConfig?.colors}
          />
        )

      case 'gauge':
        return (
          <GaugeComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData}
            valueKey={config.gaugeConfig?.valueKey || 'value'}
            min={config.gaugeConfig?.min}
            max={config.gaugeConfig?.max}
            unit={config.gaugeConfig?.unit}
            thresholds={config.gaugeConfig?.thresholds}
          />
        )

      // =====================================================
      // FASE 2: COMPONENTES MUY ÚTILES
      // =====================================================

      case 'area-chart':
        return (
          <AreaChartComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            xAxisKey={config.areaChartConfig?.xAxisKey || 'fecha'}
            yAxisKeys={config.areaChartConfig?.yAxisKeys || ['valor']}
            stacked={config.areaChartConfig?.stacked}
            areaType={config.areaChartConfig?.areaType}
            showGrid={config.areaChartConfig?.showGrid}
            colors={config.areaChartConfig?.colors}
          />
        )

      case 'stacked-bar': {
        // Misma lógica que ComponentRenderer: fieldAliases del query builder → nombres de columna en los datos
        const fieldAliases = config.customQuery?.config?.fieldAliases as
          | Record<string, string>
          | undefined
        const resolveKey = (k: string) => (fieldAliases && fieldAliases[k]) || k

        const xAxisKeyConfig = config.stackedBarConfig?.xAxisKey || 'categoria'
        const xAxisKey = resolveKey(xAxisKeyConfig)
        const stackCategoryKey = config.stackedBarConfig?.stackCategoryKey
        const valueKey = config.stackedBarConfig?.valueKey
          ? resolveKey(config.stackedBarConfig.valueKey)
          : undefined
        const rawData = previewData || []
        let chartData = rawData
        const yAxisKeysConfig = config.stackedBarConfig?.yAxisKeys || ['valor']
        let yAxisKeys = yAxisKeysConfig.map(resolveKey)
        if (stackCategoryKey && valueKey && rawData.length > 0) {
          const stackCatResolved = resolveKey(stackCategoryKey)
          const byCategory = new Map<string, Record<string, number>>()
          const seriesNames = new Set<string>()
          rawData.forEach((row: Record<string, unknown>) => {
            const cat = row[xAxisKey] != null ? String(row[xAxisKey]) : ''
            const series = row[stackCatResolved] != null ? String(row[stackCatResolved]) : ''
            const val = Number(row[valueKey]) || 0
            if (!byCategory.has(cat)) byCategory.set(cat, {})
            seriesNames.add(series)
            const rec = byCategory.get(cat)!
            rec[series] = (rec[series] || 0) + val
          })
          chartData = Array.from(byCategory.entries()).map(([cat, seriesValues]) => ({
            [xAxisKey]: cat,
            ...seriesValues,
          }))
          yAxisKeys = Array.from(seriesNames)
        }
        return (
          <StackedBarComponent
            title={config.metadata?.title || 'Sin título'}
            data={chartData}
            xAxisKey={xAxisKey}
            yAxisKeys={yAxisKeys}
            layout={config.stackedBarConfig?.layout}
            showGrid={config.stackedBarConfig?.showGrid}
            colors={config.stackedBarConfig?.colors}
          />
        )
      }

      case 'scatter-plot':
        return (
          <ScatterPlotComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            xAxisKey={config.scatterPlotConfig?.xAxisKey || 'x'}
            yAxisKey={config.scatterPlotConfig?.yAxisKey || 'y'}
            zAxisKey={config.scatterPlotConfig?.zAxisKey}
            groupKey={config.scatterPlotConfig?.groupKey}
            showGrid={config.scatterPlotConfig?.showGrid}
            colors={config.scatterPlotConfig?.colors}
          />
        )

      // =====================================================
      // FASE 3: NICE TO HAVE
      // =====================================================

      case 'heatmap':
        return (
          <HeatmapComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            xAxisKey={config.heatmapConfig?.xAxisKey || 'x'}
            yAxisKey={config.heatmapConfig?.yAxisKey || 'y'}
            valueKey={config.heatmapConfig?.valueKey || 'value'}
            colorScale={config.heatmapConfig?.colorScale}
            showValues={config.heatmapConfig?.showValues}
          />
        )

      case 'treemap':
        return (
          <TreemapComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            nameKey={config.treemapConfig?.nameKey || 'name'}
            valueKey={config.treemapConfig?.valueKey || 'value'}
            colors={config.treemapConfig?.colors}
          />
        )

      case 'radar-chart':
        return (
          <RadarChartComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            subjectKey={config.radarChartConfig?.subjectKey || 'subject'}
            valueKeys={config.radarChartConfig?.valueKeys || ['value']}
            colors={config.radarChartConfig?.colors}
            fillOpacity={config.radarChartConfig?.fillOpacity}
          />
        )

      case 'radial-bar':
        return (
          <RadialBarComponent
            title={config.metadata?.title || 'Sin título'}
            data={previewData || []}
            nameKey={config.radialBarConfig?.nameKey || 'name'}
            valueKey={config.radialBarConfig?.valueKey || 'value'}
            maxValue={config.radialBarConfig?.maxValue}
            colors={config.radialBarConfig?.colors}
          />
        )

      default:
        return (
          <div className="p-8 text-center">
            <p className="text-gray-500 mb-2">Tipo de componente no soportado</p>
            <p className="text-sm text-gray-400">Tipo: {config.componentType}</p>
          </div>
        )
    }
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver a Configuración
      </button>

      <h2 className="text-2xl font-bold mb-6">Paso 4: Vista Previa</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Preview del componente */}
        <div className="lg:col-span-2">
          <h3 className="font-semibold mb-3">Cómo se verá en el Dashboard:</h3>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            {renderPreview()}
          </div>
        </div>

        {/* Información de configuración */}
        <div>
          <h3 className="font-semibold mb-3">Configuración Actual:</h3>
          <div className="bg-white border rounded-lg p-4 space-y-3 text-sm">
            <div>
              <span className="font-medium">Tipo:</span>
              <p className="text-gray-600 capitalize">{config.componentType}</p>
            </div>
            <div>
              <span className="font-medium">Fuente de datos:</span>
              <p className="text-gray-600">{config.dataSource}</p>
            </div>
            {config.dataField && (
              <div>
                <span className="font-medium">Campo:</span>
                <p className="text-gray-600">{config.dataField}</p>
              </div>
            )}
            {config.aggregation && (
              <div>
                <span className="font-medium">Operación:</span>
                <p className="text-gray-600">{config.aggregation}</p>
              </div>
            )}
            {config.filters && Object.keys(config.filters).length > 0 && (
              <div>
                <span className="font-medium">Filtros:</span>
                <p className="text-gray-600">{Object.keys(config.filters).length} aplicados</p>
              </div>
            )}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Tip:</strong> Este componente será reutilizable en cualquier dashboard que crees.
            </p>
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex gap-4 mt-8">
        <button
          onClick={onPrev}
          className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50"
        >
          ← Modificar Configuración
        </button>
        <button
          onClick={onNext}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700"
        >
          Guardar y Publicar →
        </button>
      </div>
    </div>
  )
}
